import * as React from 'react';
import {
  Typography,
  Box,
  Pagination,
  Grid,
  Chip,
  Paper,
} from '@mui/material';
import { connect } from "react-redux";
import AddIcon from '@mui/icons-material/Add';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getWhatsappCampaigns } from '../../app/store/actions/adminActions';
import {
  PlatformPage,
  PlatformPageHeader,
  PlatformCard,
  PlatformSearchBar,
  PlatformButton,
  PlatformEmptyState,
  PlatformLoadingBlock,
} from '../../app/components/platform';
import { platformColors, platformFonts } from '../../app/components/tickitPlatformTheme';

export const ViewAllCampaigns = ({ getWhatsappCampaigns, campaigns, isLoading }) => {
  const navigate = useNavigate();

  const initialState = { search: "", page: 1, limit: 10 };
  const [formData, setFormData] = React.useState(initialState);
  const { search, page, limit } = formData;

  React.useEffect(() => {
    getWhatsappCampaigns({ page, limit, search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (_event, value) => {
    setFormData({ ...formData, page: value });
    getWhatsappCampaigns({ page: value, limit, search });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSearch = () => {
    setFormData({ ...formData, page: 1 });
    getWhatsappCampaigns({ page: 1, limit, search });
  };

  const handleClearFilter = () => {
    setFormData(initialState);
    getWhatsappCampaigns({ page: 1, limit: initialState.limit, search: initialState.search });
  };

  const totalDocs = campaigns?.totalDocs ?? campaigns?.docs?.length ?? 0;

  return (
    <PlatformPage>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <PlatformPageHeader
          title="WhatsApp campaigns"
          subtitle="Manage marketing campaigns and templates"
          actions={
            <PlatformButton
              variant="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => navigate("/Admin/createCampaign")}
            >
              Create campaign
            </PlatformButton>
          }
        />

        <PlatformCard title="Search" sx={{ mb: 2.5 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <PlatformSearchBar
                value={search}
                onChange={(e) => handleChange({ target: { name: 'search', value: e.target.value } })}
                placeholder="Search campaigns…"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <PlatformButton variant="primary" onClick={handleSearch} sx={{ width: '100%' }}>
                Search
              </PlatformButton>
            </Grid>
            <Grid item xs={6} md={2}>
              <PlatformButton variant="ghost" onClick={handleClearFilter} sx={{ width: '100%' }}>
                Clear
              </PlatformButton>
            </Grid>
          </Grid>
        </PlatformCard>

        <PlatformCard
          title="Results"
          headerAction={
            <Typography
              component="span"
              aria-live="polite"
              sx={{ fontFamily: platformFonts.body, fontSize: '0.8125rem', color: 'var(--platform-text-3)' }}
            >
              {isLoading ? 'Loading…' : `${totalDocs} campaign${totalDocs === 1 ? '' : 's'}`}
            </Typography>
          }
        >
          {isLoading ? (
            <PlatformLoadingBlock />
          ) : campaigns?.docs?.length > 0 ? (
            <>
              <Grid container spacing={2}>
                {campaigns.docs.map((campaign) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={campaign._id}>
                    <Paper
                      elevation={0}
                      component="button"
                      type="button"
                      onClick={() => navigate(`/campaign/dashboard/${campaign._id}`)}
                      sx={{
                        p: 2.5,
                        width: '100%',
                        textAlign: 'left',
                        borderRadius: '12px',
                        border: `1px solid ${platformColors.border}`,
                        bgcolor: platformColors.surface,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': {
                          borderColor: platformColors.brand,
                          boxShadow: '0 8px 24px rgba(70,16,155,0.12)',
                          transform: 'translateY(-2px)',
                        },
                        '&:focus-visible': {
                          outline: `2px solid ${platformColors.brand}`,
                          outlineOffset: 2,
                        },
                      }}
                    >
                      {campaign.image && (
                        <Box
                          component="img"
                          src={campaign.image}
                          alt={campaign.title}
                          sx={{
                            width: '100%',
                            height: 160,
                            objectFit: 'cover',
                            borderRadius: '8px',
                            mb: 1.5,
                          }}
                        />
                      )}
                      <Typography
                        sx={{
                          fontFamily: platformFonts.display,
                          fontWeight: 700,
                          fontSize: '0.9375rem',
                          color: 'var(--platform-text)',
                          mb: 0.75,
                        }}
                      >
                        {campaign.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: platformFonts.body,
                          fontSize: '13px',
                          color: 'var(--platform-text-3)',
                          mb: 1.5,
                          flexGrow: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {campaign.description}
                      </Typography>
                      <Chip
                        label={new Date(campaign.createdAt).toLocaleDateString()}
                        size="small"
                        sx={{
                          alignSelf: 'flex-start',
                          borderColor: platformColors.brand,
                          color: platformColors.brand,
                          fontFamily: platformFonts.body,
                        }}
                        variant="outlined"
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={campaigns?.pages || 1}
                  page={page}
                  onChange={handlePageChange}
                  sx={{
                    '& .Mui-selected': {
                      bgcolor: `${platformColors.brandPale} !important`,
                      color: platformColors.brand,
                    },
                  }}
                />
              </Box>
            </>
          ) : (
            <PlatformEmptyState
              title="No campaigns found"
              description="Try adjusting your search criteria or create a new campaign."
              actionLabel="Create campaign"
              onAction={() => navigate("/Admin/createCampaign")}
            />
          )}
        </PlatformCard>
      </motion.div>
    </PlatformPage>
  );
};

const mapStateToProps = (state) => ({
  campaigns: state?.records?.results?.campaigns,
  isLoading: state?.records?.isLoading,
});

export default connect(mapStateToProps, { getWhatsappCampaigns })(ViewAllCampaigns);
